import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, addDoc, updateDoc, collection, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import storage, { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const AnnouncementForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        targetAudience: "user",
        isActive: true,
        priority: "medium",
        version: "",
        imageUrl: ""
    });

    const [createdAt, setCreatedAt] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isEditMode) {
            fetchAnnouncement();
        }
    }, [id, isEditMode]);

    useEffect(() => {
        if (formData.imageUrl) {
            validateImageUrl(formData.imageUrl);
        } else {
            setImagePreview(null);
        }
    }, [formData.imageUrl]);

    const fetchAnnouncement = async () => {
        try {
            setLoading(true);
            const docRef = doc(db, "announcements", id);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                setError("Announcement not found");
                setLoading(false);
                return;
            }

            const data = docSnap.data();
            setFormData({
                title: data.title || "",
                description: data.description || "",
                targetAudience: data.targetAudience || "user",
                isActive: data.isActive !== undefined ? data.isActive : true,
                priority: data.priority || "medium",
                version: data.version || "",
                imageUrl: data.imageUrl || ""
            });
            setCreatedAt(data.createdAt || null);

            setLoading(false);
        } catch (err) {
            console.error("Error fetching announcement:", err);
            setError(err.message);
            setLoading(false);
        }
    };

    const validateImageUrl = (url) => {
        if (!url) {
            setImagePreview(null);
            return;
        }

        try {
            new URL(url);
            setImagePreview(url);
        } catch {
            setImagePreview(null);
        }
    };

    const validateForm = () => {
        const errors = {};

        // Title validation
        if (!formData.title.trim()) {
            errors.title = "Title is required";
        } else if (formData.title.length > 100) {
            errors.title = "Title must be 100 characters or less";
        }

        // Description validation
        if (!formData.description.trim()) {
            errors.description = "Description is required";
        } else if (formData.description.length > 500) {
            errors.description = "Description must be 500 characters or less";
        }

        // Version validation (if provided)
        if (formData.version && !/^\d+\.\d+\.\d+$/.test(formData.version)) {
            errors.version = "Version must be in format x.y.z (e.g., 1.2.1)";
        }

        // Image URL validation (if provided)
        if (formData.imageUrl) {
            try {
                new URL(formData.imageUrl);
            } catch {
                errors.imageUrl = "Please enter a valid URL";
            }
        }


        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Please upload a valid image file.");
            return;
        }

        const maxSizeMB = 5;
        if (file.size > maxSizeMB * 1024 * 1024) {
            alert(`File size should be less than ${maxSizeMB}MB.`);
            return;
        }

        try {
            setUploadingImage(true);
            setUploadError(null);

            const fileRef = ref(storage, `announcements/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(fileRef);

            setFormData((prev) => ({
                ...prev,
                imageUrl: downloadURL
            }));
            setImagePreview(downloadURL);
            // Clear manual URL errors
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors.imageUrl;
                return newErrors;
            });
        } catch (err) {
            console.error("Error uploading image:", err);
            setUploadError("Failed to upload image. Please try again.");
        } finally {
            setUploadingImage(false);
            // reset input value so same file can be re-selected if needed
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleRemoveImage = () => {
        setFormData((prev) => ({
            ...prev,
            imageUrl: ""
        }));
        setImagePreview(null);
        setUploadError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };


    const handleSubmit = async (e, saveAndCreateAnother = false) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const announcementData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                targetAudience: formData.targetAudience,
                isActive: formData.isActive,
                priority: formData.priority,
                updatedAt: Timestamp.now(),
                updatedBy: user?.id || user?.username || "unknown"
            };

            // Add optional fields only if they have values
            if (formData.version) {
                announcementData.version = formData.version.trim();
            }

            if (formData.imageUrl) {
                announcementData.imageUrl = formData.imageUrl.trim();
            }

            if (isEditMode) {
                // Update existing announcement
                const docRef = doc(db, "announcements", id);
                await updateDoc(docRef, announcementData);
            } else {
                // Create new announcement
                announcementData.createdAt = Timestamp.now();
                announcementData.createdBy = user?.id || user?.username || "unknown";
                await addDoc(collection(db, "announcements"), announcementData);
            }

            if (saveAndCreateAnother && !isEditMode) {
                // Reset form for new announcement
                setFormData({
                    title: "",
                    description: "",
                    targetAudience: "user",
                    isActive: true,
                    priority: "medium",
                    version: "",
                    imageUrl: ""
                });
                setImagePreview(null);
                setValidationErrors({});
                alert("Announcement saved successfully!");
            } else {
                navigate("/announcements");
            }
        } catch (err) {
            console.error("Error saving announcement:", err);
            setError(err.message);
            alert("Failed to save announcement. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="announcement-form-container">
                <div className="loading-container">
                    <span className="loader"></span>
                </div>
            </div>
        );
    }

    return (
        <div className="announcement-form-container">
            <div className="header">
                <h2>{isEditMode ? "Edit Announcement" : "Create New Announcement"}</h2>
                <button className="back-btn" onClick={() => navigate("/announcements")}>
                    ← Back to List
                </button>
            </div>

            {error && (
                <div className="error-banner">
                    <p>Error: {error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="announcement-form">
                {/* Required Fields */}
                <div className="form-section">
                    <h3 className="section-title">Required Information</h3>

                    <div className="form-group">
                        <label htmlFor="title">
                            Announcement Title <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            placeholder="e.g., Get Assistance Feature"
                            maxLength={100}
                            className={validationErrors.title ? "error" : ""}
                        />
                        <div className="field-meta">
                            <span className="char-count">{formData.title.length}/100</span>
                            {validationErrors.title && (
                                <span className="error-message">{validationErrors.title}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description">
                            Description <span className="required">*</span>
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Describe the new feature or update..."
                            rows={5}
                            maxLength={500}
                            className={validationErrors.description ? "error" : ""}
                        />
                        <div className="field-meta">
                            <span className="char-count">{formData.description.length}/500</span>
                            {validationErrors.description && (
                                <span className="error-message">{validationErrors.description}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="targetAudience">
                            Target Audience <span className="required">*</span>
                        </label>
                        <div className="radio-group">
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="targetAudience"
                                    value="user"
                                    checked={formData.targetAudience === "user"}
                                    onChange={handleInputChange}
                                />
                                <span>User</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="targetAudience"
                                    value="partner"
                                    checked={formData.targetAudience === "partner"}
                                    onChange={handleInputChange}
                                />
                                <span>Partner</span>
                            </label>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="targetAudience"
                                    value="both"
                                    checked={formData.targetAudience === "both"}
                                    onChange={handleInputChange}
                                />
                                <span>Both</span>
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                            />
                            <span>Active</span>
                        </label>
                        <p className="help-text">Only active announcements are shown to users</p>
                    </div>
                </div>

                {/* Optional Fields */}
                <div className="form-section">
                    <h3 className="section-title">Optional Information</h3>

                    <div className="form-group">
                        <label htmlFor="priority">Priority</label>
                        <select
                            id="priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleInputChange}
                        >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                        <p className="help-text">High priority announcements show as modals on app launch</p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="version">Target App Version (Optional)</label>
                        <input
                            type="text"
                            id="version"
                            name="version"
                            value={formData.version}
                            onChange={handleInputChange}
                            placeholder="e.g., 1.2.1"
                            className={validationErrors.version ? "error" : ""}
                        />
                        <div className="field-meta">
                            {validationErrors.version && (
                                <span className="error-message">{validationErrors.version}</span>
                            )}
                        </div>
                        <p className="help-text">Leave empty to show to all versions. If set, only users on this version or newer will see it.</p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="imageUrl">Announcement Image (Optional)</label>
                        <input
                            type="url"
                            id="imageUrl"
                            name="imageUrl"
                            value={formData.imageUrl}
                            onChange={handleInputChange}
                            placeholder="https://example.com/image.jpg"
                            className={validationErrors.imageUrl ? "error" : ""}
                        />
                        <div className="field-meta">
                            {validationErrors.imageUrl && (
                                <span className="error-message">{validationErrors.imageUrl}</span>
                            )}
                        </div>

                        <div className="image-upload-options">
                            <label htmlFor="imageUpload">Or upload an image file</label>
                            <input
                                type="file"
                                id="imageUpload"
                                accept="image/*"
                                onChange={handleImageUpload}
                                ref={fileInputRef}
                            />
                            {uploadingImage && <p className="upload-status">Uploading image...</p>}
                            {uploadError && <p className="error-message">{uploadError}</p>}
                            <p className="help-text">Supported formats: JPG, PNG (max 5MB). Uploading will replace the URL above.</p>
                        </div>

                        {imagePreview && (
                            <div className="image-preview">
                                <img src={imagePreview} alt="Preview" onError={() => setImagePreview(null)} />
                                <button
                                    type="button"
                                    className="remove-image-btn"
                                    onClick={handleRemoveImage}
                                >
                                    Remove Image
                                </button>
                            </div>
                        )}
                    </div>

                </div>

                {/* Read-only Created Date (for edit mode) */}
                {isEditMode && createdAt && (
                    <div className="form-section">
                        <div className="form-group">
                            <label>Created Date</label>
                            <p className="read-only-field">
                                {createdAt._seconds
                                    ? new Date(createdAt._seconds * 1000).toLocaleString()
                                    : createdAt.toMillis
                                        ? new Date(createdAt.toMillis()).toLocaleString()
                                        : "N/A"}
                            </p>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                    <button type="submit" className="save-btn" disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                    {!isEditMode && (
                        <button
                            type="button"
                            className="save-and-create-btn"
                            onClick={(e) => handleSubmit(e, true)}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save & Create Another"}
                        </button>
                    )}
                    <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => navigate("/announcements")}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                </div>
            </form>

            <style>{`
                .announcement-form-container {
                    width: 100%;
                    padding: 2rem;
                    box-sizing: border-box;
                }

                .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .header h2 {
                    font-size: 2rem;
                    color: var(--text-primary, #1f2937);
                    font-weight: 700;
                    margin: 0;
                }

                .back-btn {
                    padding: 0.75rem 1.5rem;
                    background: #6b7280;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .back-btn:hover {
                    background: #4b5563;
                }

                .error-banner {
                    background: #fee2e2;
                    color: #991b1b;
                    padding: 1rem;
                    border-radius: 8px;
                    margin-bottom: 1.5rem;
                }

                .announcement-form {
                    background: var(--bg-card, #ffffff);
                    padding: 2rem;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px var(--shadow, rgba(0, 0, 0, 0.1));
                }

                .form-section {
                    margin-bottom: 2.5rem;
                }

                .section-title {
                    font-size: 1.5rem;
                    color: var(--text-primary, #1f2937);
                    margin-bottom: 1.5rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 2px solid var(--border-color, #e5e7eb);
                }

                .form-group {
                    margin-bottom: 1.5rem;
                }

                .form-group label {
                    display: block;
                    font-weight: 600;
                    color: var(--text-primary, #374151);
                    margin-bottom: 0.5rem;
                }

                .required {
                    color: #dc2626;
                }

                .form-group input[type="text"],
                .form-group input[type="url"],
                .form-group select,
                .form-group textarea {
                    width: 100%;
                    padding: 0.75rem;
                    border: 1px solid var(--border-color, #d1d5db);
                    border-radius: 8px;
                    font-size: 1rem;
                    font-family: inherit;
                    transition: border-color 0.3s ease;
                    background: var(--bg-input, #ffffff);
                    color: var(--text-primary, #111827);
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #3b82f6;
                }

                .form-group input.error,
                .form-group textarea.error {
                    border-color: #dc2626;
                }

                .field-meta {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 0.5rem;
                    font-size: 0.875rem;
                }

                .char-count {
                    color: var(--text-muted, #6b7280);
                }

                .error-message {
                    color: #dc2626;
                }

                .help-text {
                    margin-top: 0.5rem;
                    font-size: 0.875rem;
                    color: var(--text-muted, #6b7280);
                }

                .radio-group {
                    display: flex;
                    gap: 1.5rem;
                    flex-wrap: wrap;
                }

                .radio-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    font-weight: normal;
                }

                .radio-label input[type="radio"] {
                    width: auto;
                    cursor: pointer;
                }

                .toggle-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                }

                .toggle-label input[type="checkbox"] {
                    width: auto;
                    cursor: pointer;
                }

                .image-preview {
                    margin-top: 1rem;
                    max-width: 320px;
                    border: 1px solid var(--border-color, #e5e7eb);
                    border-radius: 8px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                    padding: 0.75rem;
                    background: var(--bg-tertiary, #f9fafb);
                }

                .image-preview img {
                    width: 100%;
                    height: auto;
                    display: block;
                    border-radius: 4px;
                }

                .image-upload-options {
                    margin-top: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .image-upload-options input[type="file"] {
                    border: 1px dashed var(--border-color, #d1d5db);
                    border-radius: 8px;
                    padding: 0.75rem;
                    cursor: pointer;
                    background: var(--bg-input, #ffffff);
                }

                .upload-status {
                    font-size: 0.9rem;
                    color: #2563eb;
                }

                .remove-image-btn {
                    align-self: flex-start;
                    padding: 0.4rem 1rem;
                    border: none;
                    border-radius: 6px;
                    background: #dc2626;
                    color: white;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }

                .remove-image-btn:hover {
                    background: #b91c1c;
                }

                .read-only-field {
                    padding: 0.75rem;
                    background: var(--bg-tertiary, #f9fafb);
                    border: 1px solid var(--border-color, #e5e7eb);
                    border-radius: 8px;
                    color: var(--text-muted, #6b7280);
                }

                .form-actions {
                    display: flex;
                    gap: 1rem;
                    margin-top: 2rem;
                    padding-top: 2rem;
                    border-top: 2px solid var(--border-color, #e5e7eb);
                    flex-wrap: wrap;
                }

                .save-btn,
                .save-and-create-btn,
                .cancel-btn {
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 8px;
                    font-size: 1rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .save-btn {
                    background: #3b82f6;
                    color: white;
                }

                .save-btn:hover:not(:disabled) {
                    background: #2563eb;
                }

                .save-and-create-btn {
                    background: #10b981;
                    color: white;
                }

                .save-and-create-btn:hover:not(:disabled) {
                    background: #059669;
                }

                .cancel-btn {
                    background: #6b7280;
                    color: white;
                }

                .cancel-btn:hover:not(:disabled) {
                    background: #4b5563;
                }

                .save-btn:disabled,
                .save-and-create-btn:disabled,
                .cancel-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .loading-container {
                    text-align: center;
                    padding: 3rem;
                    background: white;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .loading-container .loader {
                  width: 48px;
                  height: 48px;
                  border-radius: 50%;
                  display: inline-block;
                  position: relative;
                  border: 2px solid #FF3D00;
                  box-sizing: border-box;
                  animation: rotation 1s linear infinite;
                }
                .loading-container .loader::after {
                  content: '';  
                  box-sizing: border-box;
                  position: absolute;
                  left: 4px;
                  top: 4px;
                  border: 2px solid #000;
                  width: 12px;
                  height: 12px;
                  border-radius: 50%;
                }
                [data-theme="dark"] .loading-container .loader::after {
                  border: 2px solid #FFF;
                }
                @keyframes rotation {
                  0% {
                    transform: rotate(0deg);
                  }
                  100% {
                    transform: rotate(360deg);
                  }
                }

                @media (max-width: 768px) {
                    .announcement-form-container {
                        padding: 1rem;
                    }

                    .announcement-form {
                        padding: 1.5rem;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .save-btn,
                    .save-and-create-btn,
                    .cancel-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default AnnouncementForm;

